import { faAngleLeft, faCheck, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../../../hooks/Form';
import { useCurrentUser } from '../../../../UserProvider';
import { api } from '../../../../utils/api';
import { DELETE_ACCOUNT_STEPS, useDeleteAccountContext } from '../DeleteAccount';

const schema = z.object({
    verificationCode: z.string()
        .nonempty('Verification code is required')
});

function createInitialValues() {
    return {
        verificationCode: ''
    };
}

function createInitialErrors() {
    return {
        verificationCode: null
    };
}

export default function DeleteAccountStart() {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const { setStep } = useDeleteAccountContext();

    const { values, setFieldValue, errors, setErrors, setFieldError, error, setError, isLoading, setIsLoading } = useForm({
        initialValues: createInitialValues(),
        initialErrors: createInitialErrors()
    });

    const handleVerificationCode = async () => {
        try {
            setError(null);
            setErrors(createInitialErrors());

            setIsLoading(true);

            const result = schema.safeParse(values);

            if (!result.success) {
                result.error.issues.forEach(err => {
                    setFieldError(err.path[0], err.message);
                });

                return;
            }

            await api.put('/deletion_requests', {
                email: currentUser.email,
                code: result.data.verificationCode
            }, {
                validateStatus: (status) => status === 204
            });

            navigate('/logout', { replace: true });

        } catch {
            setError('Invalid verification code.');

        } finally {
            setIsLoading(false);
        }
    };

    /** @param {React.FormEvent<HTMLFormElement>} event */
    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        handleVerificationCode();
    };

    useEffect(() => {
        if (values.verificationCode.length === 6) {
            handleVerificationCode();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.verificationCode]);

    return (
        <Fragment>
            <div className='container'>
                <nav className='nav'>
                    <button type='button' onClick={() => setStep(DELETE_ACCOUNT_STEPS.START)} className='nav__button button button--white button--link button--icon'>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    <p className='nav__title'>
                        Delete your account
                    </p>
                </nav>
                <div className='panel'>
                    <form id='verification-form' onSubmit={handleSubmit} className='panel__form form'>
                        <p className='form__title'>
                            Check you email! To confirm verify the code you have received
                        </p>
                        <div className='text-field text-field--black form__field'>
                            <label htmlFor='verification-code' className='text-field__label'>
                                Verification code
                            </label>
                            <input
                                id='verification-code'
                                name='verification-code'
                                type='text'
                                placeholder='• • • • • •'
                                value={values.verificationCode}
                                maxLength={6}
                                required
                                onChange={(e) => setFieldValue('verificationCode', e.target.value)}
                                className='text-field__input'
                            />
                            {errors.verificationCode && (
                                <p className='text-field__error'>
                                    {errors.verificationCode}
                                </p>
                            )}
                        </div>
                        {error && (
                            <p className='error error--white'>
                                {error}
                            </p>
                        )}
                    </form>
                </div>
                <div className='button-group'>
                    <button type='button' onClick={() => setStep(DELETE_ACCOUNT_STEPS.START)} className='button button--primary button--outline'>
                        Cancel <FontAwesomeIcon icon={faClose} />
                    </button>
                    <button type='submit' form='verification-form' disabled={isLoading} className='button button--primary button--fill'>
                        {isLoading ? <>Loading...</> : <>Verify <FontAwesomeIcon icon={faCheck} /></>}
                    </button>
                </div>
            </div>
        </Fragment>
    );
}